import memoize from 'lodash/memoize';

import { AuthTokenObject } from './AuthToken';
import IdentityType from './constants/IdentityType';
import config from './config.json';

import { Identity, IdentityResponse } from '.';

interface IdpConfig {
    appId: string;
    [key: string]: any;
}

interface DaltonConfig {
    baseURL: string;
    idp: Record<IdentityType, IdpConfig>;
}

interface IdentityLike {
    identityType: IdentityType;
}

export const checkAuthorization = (tokenObj: AuthTokenObject | null | string, ...authorizations: string[]) => {
    let obj: AuthTokenObject | null;

    if (typeof tokenObj === 'string') {
        obj = parseAuthToken(tokenObj);
    } else {
        obj = tokenObj;
    }

    if (!obj || !Array.isArray(obj.authorizations)) {
        return false;
    }

    for (const authz of authorizations) {
        if (!obj.authorizations.includes(authz)) {
            return false;
        }
    }

    return true;
};

/**
 * Get the base URL for the Dalton API depending on the adultswim env
 */
export function getBaseURL() {
    return getConfig().baseURL;
}

export function getConfig(): DaltonConfig {
    const env = window.AS.Environment.getEnv();
    return config[env] || config.production;
}

export function getClientApplicationHeader() {
    return [
        navigator.userAgent,
        `@adultswim/auth-sdk ${process.env.VERSION}`,
    ]
        .map(str => typeof str === 'string' ? str.trim() : '')
        .join('|');
}

export function getOauthCallback(): string {
    const baseURL = window.AS.Environment.getBaseURL(true).replace(/\/+$/, '');
    return baseURL + '/login/oauth_callback/';
}

export function withAuthorization(authorization: string) {
    return (tokenObj: AuthTokenObject | null) => checkAuthorization(tokenObj, authorization);
}

export function isIdentity(identity: any): identity is Identity {
    return !!identity && typeof identity.identityType === 'string';
}

export function isIdentityResponse(identity: any): identity is IdentityResponse {
    return !!identity && typeof identity.identityType === 'string' && typeof identity.id === 'string';
}

export function isSocialIdentity(identity?: IdentityLike): boolean {
    if (!identity) {
        return false;
    }
    switch (identity.identityType) {
        case IdentityType.FACEBOOK:
        case IdentityType.TWITTER:
            return true;
        default:
            return false;
    }
}

export const parseAuthToken = memoize((authToken: string | null): AuthTokenObject | null => {
    if (!authToken || typeof authToken !== 'string') {
        return null;
    }

    const [TID, authz, signature, timestamp] = authToken.split('|');

    return {
        TID,
        authorizations: typeof authz === 'string' ? authz.replace(/\^$/, '').split('^').map(grant => grant.split('*')[0]) : [],
        signature,
        timestamp: parseInt(timestamp, 10),
        rawToken: authToken,
    };
});
